import React, { useState } from 'react';
import * as XLSX from 'xlsx-js-style';
import { Client, Suscripcion } from '../../types/index';
import { useUser } from '@clerk/clerk-react';
import { getValidClients, getNextMonthAndYear } from '../../utils/utils';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createOrFetchSepaTransaction as createOrFetchSepaTransactionService } from '../../services/api/sepaService';
import { useBranding } from '../../contexts/BrandingContext';



interface DownloadExcelProps {
    clients: Client[];
    transactionStatus: boolean
}

const DownloadExcel: React.FC<DownloadExcelProps> = ({ clients, transactionStatus }) => {
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [validRecordsCount, setValidRecordsCount] = useState(0);
    const [selectedDate, setSelectedDate] = useState('');
    const [filteredClients, setFilteredClients] = useState<Client[]>(clients);
    const { user } = useUser();
    const { branding } = useBranding();

    // Nombre de la empresa. Va en la remesa como presentador, como acreedor y en
    // el concepto de cada adeudo, para que el titular de la cuenta reconozca el
    // cargo. Antes estaba escrito a mano ("Benefisio Arosa SL" en la cabecera y
    // "FISIOTERAPIA SALUDANDO" en el concepto), así que cualquier otro centro
    // emitía recibos a nombre ajeno.
    //
    // Se usa el tenant raíz, no la sucursal activa: el CIF y la cuenta son de la
    // matriz, así que es su razón social la que debe figurar como acreedor.
    const nombreTenant = (branding?.rootTenantName || branding?.name || '').trim();

    // El campo de concepto de una remesa SEPA admite 140 caracteres.
    const conceptoSepa = (texto: string) => texto.replace(/\s+/g, ' ').trim().slice(0, 140);

    // Obtener los días disponibles del próximo mes
    const getNextMonthDays = () => {
        const today = new Date();
        const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        const daysInMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0).getDate();
        return Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const date = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), day);
            return {
                day,
                formattedDate: `${day.toString().padStart(2, '0')}/${(nextMonth.getMonth() + 1).toString().padStart(2, '0')}/${nextMonth.getFullYear()}`
            };
        });
    };

    const handleDownload = () => {
        // Sin nombre de centro la remesa saldría con el acreedor en blanco y el
        // banco la rechazaría. Mejor no generar el fichero que generarlo mal.
        if (!nombreTenant) {
            toast.error('No se ha podido cargar el nombre del centro. Recarga la página antes de generar la remesa.');
            return;
        }

        const nextMonth = new Date(new Date().setMonth(new Date().getMonth() + 1)).getMonth() + 1;
        // console.log("currentMonth + 1",nextMonth);
        const validClients = getValidClients(clients, nextMonth);

        console.log("validClients en download excel", validClients);
        setFilteredClients(validClients);
        setValidRecordsCount(validClients.length);
        setShowConfirmation(true);
    };

    

    const aplicarEstilosCebecera = (worksheet: any, numeroFila: number, columnaInicio: number, columnaFin: number, cabecera: number) => {
        for (let C = columnaInicio; C <= columnaFin; C++) {
            const cellRef = XLSX.utils.encode_cell({ r: numeroFila, c: C });
            if (!worksheet[cellRef]) worksheet[cellRef] = { t: 's', v: '' };
            // console.log("cabecera",cabecera);
            switch (cabecera) {
                case 1:{
                    worksheet[cellRef].s = {
                        fill: {
                            patternType: "solid",
                            fgColor: { rgb: "cccccc" }
                        },
                        border: {
                            top: { style: "thin", color: { rgb: "000000" } },
                            bottom: { style: "thin", color: { rgb: "000000" } },
                            ...(C === columnaInicio ? { left: { style: "thin", color: { rgb: "000000" } } } : {}),
                            ...(C === columnaFin ? { right: { style: "thin", color: { rgb: "000000" } } } : {})
                        },
                        font: {
                            name: "Arial",
                            sz: 14,
                            color: { rgb: "666666" },
                            bold: true
                        },
                        alignment: {
                            horizontal: "left",
                            vertical: "center"
                        },
                    };
                    break;
                }
                case 2:{
                    worksheet[cellRef].s = {
                        fill: {
                            patternType: "solid",
                            fgColor: { rgb: "D8D8D8" }
                        },
                        font: {
                            name: "Arial",
                            sz: 14,
                            color: { rgb: "666666" }
                        },
                        border: {
                            top: { style: "thin", color: { rgb: "000000" } },
                            bottom: { style: "thin", color: { rgb: "000000" } },
                            left: { style: "thin", color: { rgb: "000000" } },
                            right: { style: "thin", color: { rgb: "000000" } }
                        },
                        alignment: {
                            horizontal: "center",
                            vertical: "center"
                        },
                    };
                    break;
                }
                case 3:{
                    worksheet[cellRef].s = {
                        fill: {
                            patternType: "solid",
                            fgColor: { rgb: "ededed" }
                        },
                        font: {
                            name: "Arial",
                            sz: 10,
                            color: { rgb: "666666" }, 
                            bold: true
                        },
                        border: {
                            top: { style: "thin", color: { rgb: "000000" } },
                            bottom: { style: "thin", color: { rgb: "000000" } },
                            left: { style: "thin", color: { rgb: "000000" } },
                            right: { style: "thin", color: { rgb: "000000" } }
                        },
                        alignment: {
                            horizontal: "left",
                            vertical: "center"
                        },
                    };
                    break;
                }
                default: return;
            }

            // console.log("worksheet[cellRef]",worksheet[cellRef]);

        }
    };

    const handleClientIBAN = (suscripcion: Suscripcion) => {
        if (!suscripcion.activo) return '';
        if (suscripcion.iban) return suscripcion.iban;
        if (suscripcion.pagador?.IBAN) return suscripcion.pagador.IBAN;
        return '';
    }


    const findNameFromSubscriptionId = (beneficiario: string) => {
        const nextMonth = new Date(new Date().setMonth(new Date().getMonth() + 1)).getMonth() + 1; // Mes siguiente (1-12)

        const subscription = clients.flatMap(client => client.suscripciones).find(suscripcion => suscripcion._id === beneficiario);

        if (subscription) {

            // Verificar si el mes siguiente está en mesesInactivos
            if (subscription.mesesInactivos.includes(nextMonth)) {
                return ''; // Retornar cadena vacía si el mes siguiente es inactivo
            }
            
            const client = clients.find(client => client._id === subscription.cliente_id);
            return client ? ' Y ' + client.nombre : 'Cliente no encontrado';
        }
        return 'Suscripción no encontrada';
    }


    const createOrFetchSepaTransaction = async (mes: number, anio: number, createdBy: string) => {
        try {
            const data = await createOrFetchSepaTransactionService(mes, anio, createdBy);
            // console.log("SEPA transaction created or fetched successfully:", data);
            if (data.message.includes("created")) {
                toast.success("Recuerda Cerrar el mes en curso.");
            } else {
                toast.info("Cuenta por cobrar ya existe para el mes en curso.");
            }
        } catch (error) {
            // console.error("Error creating or fetching SEPA transaction:", error);
            toast.error("Error al crear o buscar la transacción SEPA.");
        }
    };

    const generateExcel = async () => {
        const { month:nextMonth, year: currentYear } = getNextMonthAndYear();
        // const nextMonth = new Date(new Date().setMonth(new Date().getMonth() + 1)).getMonth() + 1;
        // const currentYear = new Date().getFullYear();
        const createdBy = user?.fullName || 'Unknown';

        const totalImporte = filteredClients.reduce((sum, client) => {
            const activeSubscription = client.suscripciones.find(sub => 
                sub.activo && !sub.mesesInactivos?.includes(nextMonth)
            );
            return sum + (activeSubscription?.importeSuscripcion || 0);
        }, 0);

        // console.log("selected date", selectedDate);
        // Crear el workbook y worksheet inicial
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet([[""]]);  // Inicializar con una celda vacía
        XLSX.utils.book_append_sheet(workbook, worksheet, "Remesa Adeudos");

        // Datos de cabecera.
        //
        // Se han retirado los cuatro campos de identidad bancaria del acreedor
        // —identificador del presentador, identificador del acreedor, oficina
        // BBVA receptora y cuenta del acreedor— porque estaban escritos a mano
        // con los datos de Benefisio Arosa y no salen de ninguna configuración.
        // Al quitarlos la cabecera pasa de 9 columnas (A..I) a 5 (A..E), y de
        // ahí salen los reajustes de coordenadas, merges y estilos de abajo.
        const headerData = [
            ["Datos generales"],
            ["Datos cabecera presentador", "Datos cabecera acreedor"],
            [
                "NOMBRE DEL PRESENTADOR ",
                "FECHA DE COBRO ORIGINAL (dd/mm/aaaa)",
                "NOMBRE DEL ACREEDOR",
                "IMPORTE TOTAL ADEUDOS",
                "DIVISA",
            ],
            [
                nombreTenant,
                selectedDate,
                nombreTenant,
                totalImporte,
                "EUR",
            ],
            [],
            ["Datos del cobro"],
            [
                "Datos del deudor",
                "",
                "",
                "",
                "Datos del adeudo",
                "",
                "",
                "Otros datos",
            ],
            [
                "NOMBRE DEL DEUDOR",
                "CUENTA DEL DEUDOR",
                // B4 — aquí ponía "REFERENCIA ÚNICA DEL MANDATO", que guardaba
                // `suscripcion._id`: exactamente el mismo valor que "REFERENCIA
                // DEL ADEUDO" (columna F). Eran dos columnas con el mismo dato
                // duplicado, así que una se cambia por el DNI sin perder nada.
                // Se sustituye esta y no la F porque la C cae dentro del bloque
                // "Datos del deudor" (merge A10:D10), que es donde corresponde un
                // DNI, y así no se mueve ninguna coordenada del resto de la hoja.
                "DNI DEL DEUDOR",
                "FECHA FIRMA MANDATO (dd/mm/aaaa)",
                "SECUENCIA DEL ADEUDO",
                "REFERENCIA DEL ADEUDO",
                "IMPORTE DEL ADEUDO",
                "CONCEPTO",
            ]
        ];

        // Agregar datos de cabecera empezando en A4
        XLSX.utils.sheet_add_aoa(worksheet, headerData, { origin: "A4" });

         // Formatear la celda de fecha (B7) de la cabecera
        const dateCell = XLSX.utils.encode_cell({r: 6, c: 1}); // B7 en coordenadas 0-based
        if (worksheet[dateCell] && selectedDate) {
            const [day, month, year] = selectedDate.split('/').map(Number);
            worksheet[dateCell] = {
                t: 'd',
                v: new Date(Date.UTC(year, month - 1, day)),
                z: 'dd/mm/yyyy'
            };
        }

        // Establecer formato de número sin separador de miles para la celda de totalImporte (D7) de la cabecera
        const importeCell = XLSX.utils.encode_cell({r: 6, c: 3}); // D7 en coordenadas 0-based
        if (worksheet[importeCell]) {
            worksheet[importeCell].z = '0.00'; // Formato con punto decimal pero sin separador de miles
            worksheet[importeCell].t = 'n';
        }

        // Preparar datos de clientes
        const data = filteredClients.map(client => {
            const suscripcion = client.suscripciones.find(sub => 
                sub.activo && !sub.mesesInactivos?.includes(nextMonth) && !sub.pagador_id
            );

            // Convert the date to UTC
            const fechaAltaContrato = suscripcion?.fechaAltaContrato ? new Date(suscripcion.fechaAltaContrato) : null;
            const fechaFormateada = fechaAltaContrato ? 
                `${fechaAltaContrato.getUTCDate().toString().padStart(2, '0')}/${(fechaAltaContrato.getUTCMonth() + 1).toString().padStart(2, '0')}/${fechaAltaContrato.getUTCFullYear()}` 
                : '';
            return {
                "NOMBRE DEL DEUDOR": suscripcion?.pagador ? suscripcion.pagador.nombre : client.nombre,
                "CUENTA DEL DEUDOR": suscripcion ? handleClientIBAN(suscripcion) : '',
                // B4 — el DNI es el del DEUDOR, que no siempre es el cliente: se
                // resuelve igual que el nombre y que el IBAN de las dos columnas
                // de arriba. `pagador.DNI` es opcional, así que un pagador dado de
                // alta sin DNI deja la celda vacía; poner ahí el DNI del cliente
                // sería peor, porque saldría junto al nombre del pagador.
                "DNI DEL DEUDOR": suscripcion?.pagador ? (suscripcion.pagador.DNI || '') : (client.dni || ''),
                "FECHA FIRMA MANDATO (dd/mm/aaaa)": fechaFormateada,
                "SECUENCIA DEL ADEUDO": "RCUR",
                "REFERENCIA DEL ADEUDO": suscripcion?._id,
                "IMPORTE DEL ADEUDO": suscripcion?.importeSuscripcion,
                "CONCEPTO": conceptoSepa(`${nombreTenant.toUpperCase()} ${client.nombre.toUpperCase()} ${(suscripcion?.beneficiario ? findNameFromSubscriptionId(suscripcion.beneficiario).toUpperCase() : '')}`),
            };
        });

        // Agregar datos de clientes en A12
        XLSX.utils.sheet_add_json(worksheet, data, {
            origin: "A12",
            skipHeader: true,
        });


         // Formatear columnas de fechas e importes para todos los registros de clientes
        filteredClients.forEach((_, index) => {
            // Calcular la fila actual (12 es la fila inicial de datos + índice del cliente)
            const currentRow = 11 + index; // 11 porque las filas en XLSX son 0-based

            // Formatear fecha de mandato (columna D)
            const fechaMandatoCell = XLSX.utils.encode_cell({ r: currentRow, c: 3 }); // D12 onwards
            if (worksheet[fechaMandatoCell] && worksheet[fechaMandatoCell].v) {
                // Asumiendo que la fecha viene en formato dd/mm/yyyy
                const [day, month, year] = worksheet[fechaMandatoCell].v.split('/').map(Number);
                worksheet[fechaMandatoCell] = {
                    t: 'd',
                    v: new Date(Date.UTC(year, month - 1, day)),
                    z: 'dd/mm/yyyy'
                };
            }

            // Formatear importe (columna G)
            const importeCell = XLSX.utils.encode_cell({ r: currentRow, c: 6 }); // G12 onwards
            if (worksheet[importeCell] && worksheet[importeCell].v) {
                worksheet[importeCell] = {
                    t: 'n',
                    v: worksheet[importeCell].v,
                    z: '0.00'
                };
            }
        });

        // Última columna de cada bloque, en índice 0-based. La cabecera ocupa
        // A..E desde que se quitaron los datos bancarios del acreedor; el bloque
        // de cobros sigue en A..H.
        const ULT_COL_CABECERA = 4; // E
        const ULT_COL_COBROS = 7;   // H

        // Configurar merge cells
        worksheet["!merges"] = [
            // "Datos cabecera presentador" ya solo cubre A5: sin identificador ni
            // oficina, el presentador es una única columna y no hay nada que unir.
            { s: { r: 4, c: 1 }, e: { r: 4, c: ULT_COL_CABECERA } },  // B5:E5
            { s: { r: 8, c: 0 }, e: { r: 8, c: ULT_COL_COBROS } },    // A9:H9
            { s: { r: 9, c: 0 }, e: { r: 9, c: 3 } },                 // A10:D10
            { s: { r: 9, c: 4 }, e: { r: 9, c: 6 } },                 // E10:G10
        ];


        //Aplicando estilos a las celdas de primera cabecera
        aplicarEstilosCebecera(worksheet, 3, 0, ULT_COL_CABECERA, 1);  // Fila 4 (índice 3)
        aplicarEstilosCebecera(worksheet, 8, 0, ULT_COL_COBROS, 1);    // Fila 9 (índice 8)

        //Aplicando estilos a las celdas de segunda cabecera
        // A5
        aplicarEstilosCebecera(worksheet, 4, 0, 0, 2);
        // B5:E5
        aplicarEstilosCebecera(worksheet, 4, 1, ULT_COL_CABECERA, 2);
        // A10:D10
        aplicarEstilosCebecera(worksheet, 9, 0, 3, 2);
        // E10:G10
        aplicarEstilosCebecera(worksheet, 9, 4, ULT_COL_COBROS, 2);

        //Aplicando estilos a las celdas de tercera cabecera
        //A6:E6
        aplicarEstilosCebecera(worksheet, 5, 0, ULT_COL_CABECERA, 3);
        //A11:H11
        aplicarEstilosCebecera(worksheet, 10, 0, ULT_COL_COBROS, 3);


        // Configurar anchos de columna: manda el bloque más ancho, el de cobros.
        worksheet["!cols"] = Array(ULT_COL_COBROS + 1).fill({ wch: 30 });

        // Configurar altura de filas
        worksheet["!rows"] = Array(50).fill({ hpx: 30 });

        // Guardar el archivo
        console.log("Workbook",workbook);
        const excelBuffer = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'array',
            bookSST: true,
            cellStyles: true
        });

        // Crear Blob y descargar
        const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        // Crear URL y link temporal
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Remesa.xlsx';
        
        // Simular click y limpiar
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Crear o buscar la transacción SEPA
        await createOrFetchSepaTransaction(nextMonth, currentYear, createdBy);

        setShowConfirmation(false);
    };

    

    return (
        <div>
            <button 
                onClick={handleDownload} 
                disabled={transactionStatus} 
                className={`${transactionStatus ? 'bg-gray-500' : 'bg-blue-500'} text-sm text-white px-4 py-2 rounded`}
            >
                Descargar Excel
            </button>
            {showConfirmation && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-white p-4 rounded shadow-md max-w-md w-full mx-4">
                    <p>{`Se van a generar ${validRecordsCount} registros en el fichero Excel.`}</p>
                    <div className="mt-4">
                    <label className="block mb-2">Seleccione la fecha de cobro:</label>
                    <select 
                        className="border p-2 rounded w-full"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)} 
                    >
                        <option value="">Seleccione un día</option>
                        {getNextMonthDays().map(({ day, formattedDate }) => (
                            <option key={day} value={formattedDate}>
                            {formattedDate}
                            </option>
                        ))}
                    </select>
                    </div>
                    <div className="mt-4 flex justify-end gap-2">
                    <button
                        onClick={() => setShowConfirmation(false)}
                        className="bg-red-500 text-white px-4 py-2 rounded"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={generateExcel}
                        className="bg-green-500 text-white px-4 py-2 rounded"
                        disabled={!selectedDate}
                    >
                        Confirmar
                    </button>
                    </div>
                </div>
                </div>
            )}
    </div>
    );
};

export default DownloadExcel;